import React, {useState} from 'react';
import {Button, FormGroup, Input, Label} from 'reactstrap';
import {v4 as uuidv4} from 'uuid';
import {toast} from 'react-toastify';
import {CameraIcon} from 'lucide-react';
import Icon from '../shared/Icon.tsx';
import {EntityManipulationMethods} from '../../types.ts';
import {
    CameraMode,
    SceneCamera,
    SceneEvents,
    SceneSettings,
    Vector3
} from './types.ts';
import {SceneCameraState} from './sceneMessages.ts';
import SpatialCoordinateEditor from './SpatialCoordinateEditor.tsx';
import ColorPickerComponent from '../shared/ColorPicker.tsx';
import OptionalColorPicker from './OptionalColorPicker.tsx';
import getColorHexValue from './getColorHexValue.ts';

interface SceneSettingsEditorProps {
    // The sceneSettings row, if there is one yet.
    elementId: string | undefined;
    settings: SceneSettings | undefined;
    manipulation: EntityManipulationMethods;
    getCamera: () => Promise<SceneCameraState | null>;
}

// What the runtime uses for anything the settings leave out.
const DEFAULT_BACKGROUND = '#33334d';
const DEFAULT_CAMERA_POSITION: Vector3 = {x: 0, y: 5, z: -10};
const DEFAULT_CAMERA_TARGET: Vector3 = {x: 0, y: 0, z: 0};
const DEFAULT_FOV = 0.8;
const DEFAULT_FOG_DENSITY = 0.02;

const round = (value: number, places: number) =>
    Math.round(value * 10 ** places) / 10 ** places;

const roundVector = (vector: Vector3): Vector3 => ({
    x: round(vector.x, 3),
    y: round(vector.y, 3),
    z: round(vector.z, 3)
});

// The scene's own settings (one sceneSettings row): background, start camera,
// fog and the built-in light. Each change sends its top-level property whole.
// Emptied number fields aren't saved (they'd only fall back to the default).
const SceneSettingsEditor: React.FC<SceneSettingsEditorProps> = ({
    elementId,
    settings,
    manipulation,
    getCamera
}) => {
    const {mergeData} = manipulation;
    const [isGettingCamera, setIsGettingCamera] = useState(false);

    const handleChange = (property: keyof SceneSettings, value: unknown) => {
        if (elementId) {
            mergeData(elementId, {[property]: value}, SceneEvents.EntityModify);
        } else {
            // No settings yet: the first change makes the row.
            const newElementId = uuidv4();
            mergeData(
                newElementId,
                {
                    elementId: newElementId,
                    type: 'sceneSettings',
                    [property]: value
                },
                SceneEvents.EntityAdd
            );
        }
    };

    const camera = settings?.camera ?? undefined;
    const fog = settings?.fog ?? undefined;
    const defaultLight = settings?.defaultLight ?? undefined;
    const lightOn = defaultLight?.on !== false;
    const lightIntensity = defaultLight?.intensity ?? 1;

    const changeCamera = (changes: Partial<SceneCamera>) =>
        handleChange('camera', {...camera, ...changes});

    const takeCurrentView = async () => {
        setIsGettingCamera(true);
        const state = await getCamera();
        setIsGettingCamera(false);

        if (!state) {
            toast.error(
                "The scene didn't say where its camera is. Refresh it and try again; a tab running an older saved script can't do this."
            );
            return;
        }

        handleChange('camera', {
            position: roundVector(state.position),
            target: roundVector(state.target),
            fov: round(state.fov, 4),
            mode: camera?.mode ?? 'fly'
        });
    };

    const handleFovChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            changeCamera({fov: round((value * Math.PI) / 180, 4)});
        }
    };

    const handleFogDensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            handleChange('fog', {...fog, density: value});
        }
    };

    const handleLightIntensityChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            handleChange('defaultLight', {on: lightOn, intensity: value});
        }
    };

    return (
        <div className="p-2">
            <div>
                <b>Scene settings</b>
            </div>

            <FormGroup>
                <Label>Background:</Label>
                <OptionalColorPicker
                    color={settings?.background}
                    noneColor={DEFAULT_BACKGROUND}
                    noneLabel="Default"
                    noneTitle="Default background"
                    onChange={background =>
                        handleChange('background', background)
                    }
                />
            </FormGroup>

            <div className="p-2">
                <div>
                    <b>Start camera</b>
                </div>
                <FormGroup>
                    <Label>Controls:</Label>
                    <Input
                        type="select"
                        value={camera?.mode ?? 'fly'}
                        onChange={e =>
                            changeCamera({mode: e.target.value as CameraMode})
                        }
                    >
                        <option value="fly">
                            Fly (WASD, Space, Shift; drag to look)
                        </option>
                        <option value="orbit">
                            Orbit (drag to turn round the target, wheel to zoom)
                        </option>
                    </Input>
                </FormGroup>
                <Button
                    color="secondary"
                    size="sm"
                    onClick={takeCurrentView}
                    disabled={isGettingCamera}
                >
                    <Icon icon={CameraIcon} /> Use current view as start camera
                </Button>
                <SpatialCoordinateEditor
                    label="Position"
                    coordinate={camera?.position ?? DEFAULT_CAMERA_POSITION}
                    onChange={position => changeCamera({position})}
                />
                <SpatialCoordinateEditor
                    label="Looking at"
                    coordinate={camera?.target ?? DEFAULT_CAMERA_TARGET}
                    onChange={target => changeCamera({target})}
                />
                <FormGroup>
                    <Label>Field of view (degrees):</Label>
                    <Input
                        type="number"
                        step="1"
                        min="1"
                        max="179"
                        value={round(
                            ((camera?.fov ?? DEFAULT_FOV) * 180) / Math.PI,
                            1
                        )}
                        onChange={handleFovChange}
                    />
                </FormGroup>
            </div>

            <div className="p-2">
                <FormGroup check>
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={!!fog}
                            onChange={e =>
                                handleChange(
                                    'fog',
                                    e.target.checked
                                        ? {
                                              color:
                                                  settings?.background ??
                                                  DEFAULT_BACKGROUND,
                                              density: DEFAULT_FOG_DENSITY
                                          }
                                        : null
                                )
                            }
                        />{' '}
                        <b>Fog</b>
                    </Label>
                </FormGroup>
                {fog && (
                    <>
                        <FormGroup>
                            <Label>Fog color:</Label>
                            <ColorPickerComponent
                                color={getColorHexValue(
                                    fog.color ?? DEFAULT_BACKGROUND
                                )}
                                onChange={color =>
                                    handleChange('fog', {...fog, color})
                                }
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Density (about 0.01 light to 0.1 thick):
                            </Label>
                            <Input
                                type="number"
                                step="0.005"
                                min="0"
                                value={fog.density ?? DEFAULT_FOG_DENSITY}
                                onChange={handleFogDensityChange}
                            />
                        </FormGroup>
                    </>
                )}
            </div>

            <div className="p-2">
                <FormGroup check>
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={lightOn}
                            onChange={e =>
                                handleChange('defaultLight', {
                                    on: e.target.checked,
                                    intensity: lightIntensity
                                })
                            }
                        />{' '}
                        <b>Default light</b> (soft light from above)
                    </Label>
                </FormGroup>
                {lightOn && (
                    <FormGroup>
                        <Label>Intensity:</Label>
                        <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={lightIntensity}
                            onChange={handleLightIntensityChange}
                        />
                    </FormGroup>
                )}
            </div>
        </div>
    );
};

export default SceneSettingsEditor;
